import { useMemo } from 'react';
import {
    type BusinessManagementActions,
    PermissionEnum,
    type CurrentMemberFragment,
} from 'generated/graphql';
import type { User } from '~/server/types';
import { isUserLoggedIn, useUser } from './use-user';

const SYSTEM_PROFESSIONAL_ID = 26182;

/**
 * Convert BusinessManagementActions array to PermissionEnum array.
 * Both enums share the same string values for BM permissions, so we can
 * safely convert by mapping the string values.
 */
function convertTeamPermissionsToPermissionEnum(
    teamPermissions: BusinessManagementActions[]
): PermissionEnum[] {
    return teamPermissions
        .map((action) => {
            // Map BusinessManagementActions string value to PermissionEnum
            // Both enums use the same string values (e.g., 'BM_PROFILE_EDIT')
            const actionString = String(action);
            const permissionValue = Object.values(PermissionEnum).find(
                (perm) => String(perm) === actionString
            );
            return permissionValue as PermissionEnum | undefined;
        })
        .filter((perm): perm is PermissionEnum => perm !== undefined);
}

/**
 * Get the permissions for a user against a specific professional, as a team
 * member of a professional profile.
 * @param teamMembers The team member data.
 * @param professional The professional (ID or URLSegment).
 * @returns The permissions for the user as a team member of the professional.
 */
function collectPermissions(
    teamMembers: CurrentMemberFragment['TeamMembers'],
    professional: string | number
): PermissionEnum[] {
    if (!professional) {
        return [];
    }

    const team = teamMembers.find((team) => {
        if (typeof professional === 'string') {
            return team.Professional.URLSegment === professional;
        } else {
            return team.Professional.ID === professional;
        }
    });

    const systemTeamMember = teamMembers.find(
        (tm) =>
            tm.Professional.ID === SYSTEM_PROFESSIONAL_ID ||
            tm.Professional.Title === 'ArchiPro'
    );

    const permissions: PermissionEnum[] = [];

    if (team) {
        permissions.push(...team.Permissions);
        if (team.TeamPermissions && team.TeamPermissions.length > 0) {
            permissions.push(
                ...convertTeamPermissionsToPermissionEnum(team.TeamPermissions)
            );
        }
        if (team.Professional.Permissions) {
            permissions.push(...team.Professional.Permissions);
        }
    }

    if (systemTeamMember) {
        permissions.push(...systemTeamMember.Permissions);
        if (
            systemTeamMember.TeamPermissions &&
            systemTeamMember.TeamPermissions.length > 0
        ) {
            permissions.push(
                ...convertTeamPermissionsToPermissionEnum(
                    systemTeamMember.TeamPermissions
                )
            );
        }
        if (systemTeamMember.Professional.Permissions) {
            permissions.push(...systemTeamMember.Professional.Permissions);
        }
    }

    return Array.from(new Set(permissions));
}

/**
 * Check if a user has the required permission(s). If a professional is passed,
 * check if the user has the required permissions(s) for that specific professional.
 * @param user The user to check the permissions for.
 * @param permissions The permission(s) to check for.
 * @param professional An optional professional (ID or URLSegment) to check the permissions for.
 * @returns The user has the required permission(s).
 */
export function getPermissions(
    user: User | null | undefined,
    permissions: PermissionEnum | PermissionEnum[],
    professional?: string | number | null
): boolean {
    if (!user || !isUserLoggedIn(user)) {
        return false;
    }
    const { Permissions, TeamMembers } = user;
    const permissionsToCheck = Array.isArray(permissions)
        ? permissions
        : [permissions];

    // Check the user's root level permissions.
    const membersPermissions = [...Permissions];

    if (professional) {
        // If a professional is passed, check if the user has the required
        // permissions for that specific professional.
        membersPermissions.push(
            ...collectPermissions(TeamMembers, professional)
        );
    }

    return permissionsToCheck.every((permission) =>
        membersPermissions.includes(permission)
    );
}

/**
 * Hook to check if the current user has the required permission(s). If a
 * professional is passed, check if the user has the required permissions(s)
 * for that specific professional.
 * @param permissions The permission(s) to check for.
 * @param professional An optional professional (ID or URLSegment) to check the permissions for.
 * @returns The current user has the required permission(s).
 */
export function usePermissions(
    permissions: PermissionEnum | PermissionEnum[],
    professional?: string | number | null
): boolean {
    const user = useUser();

    return useMemo(
        () => getPermissions(user, permissions, professional),
        [user, permissions, professional]
    );
}

/**
 * Hook to check if the current user has certain root level permissions.
 * @returns The current user's root level permissions.
 */
export function useRootPermissions() {
    const editProfile = usePermissions(PermissionEnum.BmProfileEdit);
    const editAdvertAdmin = usePermissions(PermissionEnum.BmAdvertAdminEdit);
    const marketingView = usePermissions(PermissionEnum.BmMarketingView);

    return useMemo(() => {
        return {
            editProfile,
            editAdvertAdmin,
            marketingView,
        };
    }, [editProfile, editAdvertAdmin, marketingView]);
}

/**
 * Check if a user has a specific edit permission for a professional.
 *
 * This function checks permissions using the same logic as Business Manager:
 * - Checks TeamPermissions for the viewing professional
 * - Checks TeamPermissions for the system professional (staff override)
 * - Checks Professional.Permissions as a fallback
 *
 * Note: This deliberately excludes TeamMember.Permissions (the broader field)
 * to ensure only granular TeamPermissions control edit access.
 *
 * @param user The user to check permissions for.
 * @param professional The professional (ID or URLSegment) to check the permissions for.
 * @param permission The specific permission to check.
 * @returns Whether the user has the required permission.
 */
export function getEditPermission(
    user: User | null | undefined,
    professional: string | number,
    permission: PermissionEnum
): boolean {
    if (!user || !isUserLoggedIn(user)) {
        return false;
    }

    const { TeamMembers } = user;

    // Find team member for the viewing professional
    const team = TeamMembers.find((tm) =>
        typeof professional === 'string'
            ? tm.Professional.URLSegment === professional
            : tm.Professional.ID === professional
    );

    // Find team member for system professional (staff override)
    const systemTeam = TeamMembers.find(
        (tm) =>
            tm.Professional.ID === SYSTEM_PROFESSIONAL_ID ||
            tm.Professional.Title === 'ArchiPro'
    );

    // Collect ONLY TeamPermissions (not team.Permissions) from both
    // viewing professional and system professional
    const teamPermissions: PermissionEnum[] = [];

    if (team?.TeamPermissions && team.TeamPermissions.length > 0) {
        teamPermissions.push(
            ...convertTeamPermissionsToPermissionEnum(team.TeamPermissions)
        );
    }

    if (
        systemTeam?.TeamPermissions &&
        systemTeam.TeamPermissions.length > 0
    ) {
        teamPermissions.push(
            ...convertTeamPermissionsToPermissionEnum(
                systemTeam.TeamPermissions
            )
        );
    }

    // Check if permission exists in TeamPermissions OR Professional.Permissions
    return (
        teamPermissions.includes(permission) ||
        team?.Professional.Permissions?.includes(permission) === true
    );
}

/**
 * Hook to check if the current user has permissions to edit a professional
 * and/or their items.
 *
 * This hook checks permissions using the same logic as Business Manager:
 * - Checks TeamPermissions for the viewing professional
 * - Checks TeamPermissions for the system professional (staff override)
 * - Checks Professional.Permissions as a fallback
 *
 * Note: This deliberately excludes TeamMember.Permissions (the broader field)
 * to ensure only granular TeamPermissions control edit access.
 *
 * @param professional The professional (ID or URLSegment) to check the permissions for.
 * @returns The current user's edit permissions.
 */
export function useEditPermissions(professional: string | number) {
    const user = useUser();

    return useMemo(() => {
        if (!user || !isUserLoggedIn(user)) {
            return {
                canEditProfile: false,
                canEditProducts: false,
                canEditProjects: false,
                canEditArticles: false,
            };
        }

        return {
            canEditProfile: getEditPermission(
                user,
                professional,
                PermissionEnum.BmProfileEdit
            ),
            canEditProducts: getEditPermission(
                user,
                professional,
                PermissionEnum.BmProductsEdit
            ),
            canEditProjects: getEditPermission(
                user,
                professional,
                PermissionEnum.BmProjectEdit
            ),
            canEditArticles: getEditPermission(
                user,
                professional,
                PermissionEnum.BmArticleEdit
            ),
        };
    }, [user, professional]);
}
